// Grammar Book Progress Tracking
// Contains progress tracking structure and data
// Organized for Supabase migration and systematic data management

export const grammarBookProgress = {
  // Progress tracking structure
  structure: {
    userId: "string",
    unitId: "number",
    status: "string", // 'notStarted', 'inProgress', 'completed'
    progress: "number", // 0-100
    timeSpent: "number", // in minutes
    lastAccessed: "date",
    completedAt: "date",
    attempts: "number",
    scores: "array", // array of quiz scores
    notes: "string",
    bookmarks: "array", // array of bookmarked sections
    customData: "object" // for any additional data
  },

  // Default progress for all units
  defaultProgress: {
    1: {
      status: 'completed',
      progress: 100,
      timeSpent: 120,
      lastAccessed: new Date('2024-01-15').toISOString(),
      completedAt: new Date('2024-01-15').toISOString(),
      attempts: 1,
      scores: [95],
      notes: '',
      bookmarks: [],
      customData: {}
    },
    2: {
      status: 'completed',
      progress: 100,
      timeSpent: 180,
      lastAccessed: new Date('2024-01-20').toISOString(),
      completedAt: new Date('2024-01-20').toISOString(),
      attempts: 1,
      scores: [88],
      notes: '',
      bookmarks: [],
      customData: {}
    },
    3: {
      status: 'completed',
      progress: 100,
      timeSpent: 150,
      lastAccessed: new Date('2024-01-25').toISOString(),
      completedAt: new Date('2024-01-25').toISOString(),
      attempts: 1,
      scores: [92],
      notes: '',
      bookmarks: [],
      customData: {}
    },
    4: {
      status: 'notStarted',
      progress: 0,
      timeSpent: 0,
      lastAccessed: null,
      completedAt: null,
      attempts: 0,
      scores: [],
      notes: '',
      bookmarks: [],
      customData: {}
    },
    5: {
      status: 'notStarted',
      progress: 0,
      timeSpent: 0,
      lastAccessed: null,
      completedAt: null,
      attempts: 0,
      scores: [],
      notes: '',
      bookmarks: [],
      customData: {}
    },
    6: {
      status: 'notStarted',
      progress: 0,
      timeSpent: 0,
      lastAccessed: null,
      completedAt: null,
      attempts: 0,
      scores: [],
      notes: '',
      bookmarks: [],
      customData: {}
    },
    7: {
      status: 'notStarted',
      progress: 0,
      timeSpent: 0,
      lastAccessed: null,
      completedAt: null,
      attempts: 0,
      scores: [],
      notes: '',
      bookmarks: [],
      customData: {}
    },
    8: {
      status: 'notStarted',
      progress: 0,
      timeSpent: 0,
      lastAccessed: null,
      completedAt: null,
      attempts: 0,
      scores: [],
      notes: '',
      bookmarks: [],
      customData: {}
    }
  },

  // Progress calculation methods
  calculations: {
    // Calculate overall progress percentage
    getOverallProgress: (progressData) => {
      const totalUnits = Object.keys(progressData).length;
      const completedUnits = Object.values(progressData).filter(p => p.status === 'completed').length;
      return Math.round((completedUnits / totalUnits) * 100);
    },

    // Calculate total time spent
    getTotalTimeSpent: (progressData) => {
      return Object.values(progressData).reduce((total, p) => total + p.timeSpent, 0);
    },

    // Calculate average score
    getAverageScore: (progressData) => {
      const completedUnits = Object.values(progressData).filter(p => p.status === 'completed');
      if (completedUnits.length === 0) return 0;
      
      const totalScore = completedUnits.reduce((total, p) => {
        const avgScore = p.scores.length > 0 ? p.scores.reduce((sum, score) => sum + score, 0) / p.scores.length : 0;
        return total + avgScore;
      }, 0);
      
      return Math.round(totalScore / completedUnits.length);
    },

    // Get next recommended unit
    getNextRecommendedUnit: (progressData) => {
      const units = Object.entries(progressData);
      const notStarted = units.filter(([id, p]) => p.status === 'notStarted');
      return notStarted.length > 0 ? parseInt(notStarted[0][0]) : null;
    },

    // Get units by status
    getUnitsByStatus: (progressData, status) => {
      return Object.entries(progressData)
        .filter(([id, p]) => p.status === status)
        .map(([id, p]) => ({ unitId: parseInt(id), ...p }));
    }
  },

  // Progress tracking events
  events: {
    // Unit started
    unitStarted: (unitId, timestamp = new Date().toISOString()) => ({
      type: 'unit_started',
      unitId,
      timestamp,
      data: {
        status: 'inProgress',
        progress: 0,
        lastAccessed: timestamp
      }
    }),

    // Progress updated
    progressUpdated: (unitId, progress, timestamp = new Date().toISOString()) => ({
      type: 'progress_updated',
      unitId,
      timestamp,
      data: {
        progress,
        lastAccessed: timestamp
      }
    }),

    // Unit completed
    unitCompleted: (unitId, score, timestamp = new Date().toISOString()) => ({
      type: 'unit_completed',
      unitId,
      timestamp,
      data: {
        status: 'completed',
        progress: 100,
        completedAt: timestamp,
        scores: [score]
      }
    }),

    // Quiz attempted
    quizAttempted: (unitId, score, timestamp = new Date().toISOString()) => ({
      type: 'quiz_attempted',
      unitId,
      timestamp,
      data: {
        scores: [score]
      }
    }),

    // Bookmark added
    bookmarkAdded: (unitId, section, timestamp = new Date().toISOString()) => ({
      type: 'bookmark_added',
      unitId,
      timestamp,
      data: {
        bookmarks: [section]
      }
    })
  },

  // Progress validation
  validation: {
    // Validate progress data
    validateProgress: (progressData) => {
      const errors = [];
      
      // Check required fields
      const requiredFields = ['status', 'progress', 'timeSpent', 'attempts'];
      Object.entries(progressData).forEach(([unitId, data]) => {
        requiredFields.forEach(field => {
          if (!(field in data)) {
            errors.push(`Unit ${unitId} missing required field: ${field}`);
          }
        });
      });

      // Validate progress values
      Object.entries(progressData).forEach(([unitId, data]) => {
        if (data.progress < 0 || data.progress > 100) {
          errors.push(`Unit ${unitId} has invalid progress value: ${data.progress}`);
        }
        if (data.timeSpent < 0) {
          errors.push(`Unit ${unitId} has invalid time spent: ${data.timeSpent}`);
        }
        if (data.attempts < 0) {
          errors.push(`Unit ${unitId} has invalid attempts: ${data.attempts}`);
        }
      });

      return {
        isValid: errors.length === 0,
        errors
      };
    },

    // Validate status transitions
    validateStatusTransition: (currentStatus, newStatus) => {
      const validTransitions = {
        'notStarted': ['inProgress'],
        'inProgress': ['completed', 'notStarted'],
        'completed': ['inProgress']
      };

      return validTransitions[currentStatus]?.includes(newStatus) || false;
    }
  },

  // Progress analytics
  analytics: {
    // Get learning streak
    getLearningStreak: (progressData) => {
      const completedUnits = Object.values(progressData)
        .filter(p => p.status === 'completed')
        .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));

      if (completedUnits.length === 0) return 0;

      let streak = 1;
      const oneDay = 24 * 60 * 60 * 1000; // milliseconds

      for (let i = 1; i < completedUnits.length; i++) {
        const currentDate = new Date(completedUnits[i - 1].completedAt);
        const previousDate = new Date(completedUnits[i].completedAt);
        const diffDays = Math.abs(currentDate - previousDate) / oneDay;

        if (diffDays <= 1) {
          streak++;
        } else {
          break;
        }
      }

      return streak;
    },

    // Get learning velocity (units per week)
    getLearningVelocity: (progressData) => {
      const completedUnits = Object.values(progressData)
        .filter(p => p.status === 'completed' && p.completedAt)
        .sort((a, b) => new Date(a.completedAt) - new Date(b.completedAt));

      if (completedUnits.length < 2) return 0;

      const firstCompletion = new Date(completedUnits[0].completedAt);
      const lastCompletion = new Date(completedUnits[completedUnits.length - 1].completedAt);
      const totalWeeks = (lastCompletion - firstCompletion) / (7 * 24 * 60 * 60 * 1000);

      return totalWeeks > 0 ? Math.round((completedUnits.length / totalWeeks) * 10) / 10 : 0;
    },

    // Get difficulty analysis
    getDifficultyAnalysis: (progressData) => {
      const completedUnits = Object.values(progressData)
        .filter(p => p.status === 'completed');

      if (completedUnits.length === 0) return {};

      const avgScore = completedUnits.reduce((total, p) => {
        const unitAvg = p.scores.length > 0 ? p.scores.reduce((sum, score) => sum + score, 0) / p.scores.length : 0;
        return total + unitAvg;
      }, 0) / completedUnits.length;

      const avgAttempts = completedUnits.reduce((total, p) => total + p.attempts, 0) / completedUnits.length;

      return {
        averageScore: Math.round(avgScore),
        averageAttempts: Math.round(avgAttempts * 10) / 10,
        totalUnitsCompleted: completedUnits.length
      };
    }
  }
};

// Helper functions
export const createProgressEntry = (unitId, status = 'notStarted') => {
  return {
    unitId,
    status,
    progress: status === 'completed' ? 100 : 0,
    timeSpent: 0,
    lastAccessed: null,
    completedAt: null,
    attempts: 0,
    scores: [],
    notes: '',
    bookmarks: [],
    customData: {}
  };
};

export const updateProgress = (currentProgress, unitId, updates) => {
  return {
    ...currentProgress,
    [unitId]: {
      ...currentProgress[unitId],
      ...updates,
      lastAccessed: new Date().toISOString()
    }
  };
};

export const resetProgress = (unitId) => {
  return createProgressEntry(unitId, 'notStarted');
};

// Default export
export default grammarBookProgress;
